import { clamp } from './geometry.js';
import type { Rect, Vec } from './types.js';

/**
 * Camera state. Board coordinates are the source of truth; screen coordinates
 * are derived. Keeping the transform this simple (uniform scale, no skew) is
 * what lets the renderer hand the whole thing to `ctx.setTransform` once per
 * frame instead of transforming every node.
 */
export interface Viewport {
  /** Board coordinate shown at the top-left of the canvas. */
  x: number;
  y: number;
  zoom: number;
  /** Canvas size in CSS pixels. */
  width: number;
  height: number;
}

export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 32;

/**
 * Camera translation past this makes canvas transforms and grid iteration
 * unreliable (float64 can no longer represent a 20px grid step). Pan is
 * clamped here so "fly far away" cannot freeze the tab.
 */
export const MAX_BOARD_ABS = 4_000_000;

export function clampViewport(vp: Viewport): Viewport {
  return {
    ...vp,
    zoom: clamp(vp.zoom, MIN_ZOOM, MAX_ZOOM),
    x: clamp(vp.x, -MAX_BOARD_ABS, MAX_BOARD_ABS),
    y: clamp(vp.y, -MAX_BOARD_ABS, MAX_BOARD_ABS),
  };
}

export const ZOOM_STEPS = [
  0.02, 0.05, 0.1, 0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4, 6, 8, 16, 32,
] as const;

export function screenToBoard(vp: Viewport, p: Vec): Vec {
  return { x: vp.x + p.x / vp.zoom, y: vp.y + p.y / vp.zoom };
}

export function boardToScreen(vp: Viewport, p: Vec): Vec {
  return { x: (p.x - vp.x) * vp.zoom, y: (p.y - vp.y) * vp.zoom };
}

export function boardRectToScreen(vp: Viewport, r: Rect): Rect {
  return {
    x: (r.x - vp.x) * vp.zoom,
    y: (r.y - vp.y) * vp.zoom,
    w: r.w * vp.zoom,
    h: r.h * vp.zoom,
  };
}

/** The board rectangle currently visible, used for culling and presence. */
export function visibleRect(vp: Viewport): Rect {
  return { x: vp.x, y: vp.y, w: vp.width / vp.zoom, h: vp.height / vp.zoom };
}

/** Zoom while keeping the board point under `anchor` (screen px) fixed. */
export function zoomAt(vp: Viewport, nextZoom: number, anchor: Vec): Viewport {
  const zoom = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);
  const before = screenToBoard(vp, anchor);
  const after = { x: before.x - anchor.x / zoom, y: before.y - anchor.y / zoom };
  return clampViewport({ ...vp, zoom, x: after.x, y: after.y });
}

export function zoomBy(vp: Viewport, factor: number, anchor: Vec): Viewport {
  return zoomAt(vp, vp.zoom * factor, anchor);
}

export function nextZoomStep(zoom: number, direction: 1 | -1): number {
  if (direction === 1) {
    for (const step of ZOOM_STEPS) if (step > zoom + 1e-4) return step;
    return MAX_ZOOM;
  }
  for (let i = ZOOM_STEPS.length - 1; i >= 0; i--) {
    const step = ZOOM_STEPS[i]!;
    if (step < zoom - 1e-4) return step;
  }
  return MIN_ZOOM;
}

export function pan(vp: Viewport, dxScreen: number, dyScreen: number): Viewport {
  return clampViewport({ ...vp, x: vp.x - dxScreen / vp.zoom, y: vp.y - dyScreen / vp.zoom });
}

export interface FitOptions {
  /** Screen-pixel padding kept around the target. */
  padding?: number;
  maxZoom?: number;
}

/** Camera that frames `target`, centred, without exceeding maxZoom. */
export function fitToRect(vp: Viewport, target: Rect, options: FitOptions = {}): Viewport {
  const padding = options.padding ?? 64;
  const maxZoom = options.maxZoom ?? 1;
  const availW = Math.max(vp.width - padding * 2, 1);
  const availH = Math.max(vp.height - padding * 2, 1);
  if (target.w <= 0 || target.h <= 0) {
    return clampViewport({
      ...vp,
      x: target.x - vp.width / 2 / vp.zoom,
      y: target.y - vp.height / 2 / vp.zoom,
    });
  }
  const zoom = clamp(Math.min(availW / target.w, availH / target.h), MIN_ZOOM, Math.min(maxZoom, MAX_ZOOM));
  return clampViewport({
    ...vp,
    zoom,
    x: target.x + target.w / 2 - vp.width / 2 / zoom,
    y: target.y + target.h / 2 - vp.height / 2 / zoom,
  });
}

export function centerOn(vp: Viewport, point: Vec): Viewport {
  return clampViewport({
    ...vp,
    x: point.x - vp.width / 2 / vp.zoom,
    y: point.y - vp.height / 2 / vp.zoom,
  });
}

/** Frames the same board region a peer is looking at, for follow mode. */
export function followViewport(vp: Viewport, remote: Rect): Viewport {
  return fitToRect(vp, remote, { padding: 0, maxZoom: MAX_ZOOM });
}

/**
 * Grid pitch that stays legible: the base size multiplied by a power of the
 * subdivision until one cell is at least `minPixels` on screen.
 */
export function effectiveGridSize(baseSize: number, zoom: number, minPixels = 8): number {
  const pitch = Number.isFinite(baseSize) && baseSize > 0 ? baseSize : 20;
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : MIN_ZOOM;
  let size = pitch;
  let guard = 0;
  while (size * z < minPixels && Number.isFinite(size) && guard++ < 24) size *= 5;
  guard = 0;
  while (size * z > minPixels * 25 && size > pitch && guard++ < 24) size /= 5;
  return size;
}
